<!-- Updated FormTemplateSetup.vue with searchable dropdowns -->
<template>
  <div class="template-setup">
    <div class="template-box">
      <h3 v-if="!isEdit" class="title">Create New Form Template</h3>
      <h2 v-else class="title">Edit Form Template</h2>
      <p v-if="!isEdit" class="description">Choose a form type and give your template a name to get started</p>
      <div class="form-group">
        <label for="templateName">Template Name <span class="required-asterisk">*</span></label>
        <input
        :disabled="editData.formType === 11 || editData.formType === 12"
          id="templateName"
          v-model="templateName"
          type="text"
          placeholder="Enter template name"
          @input="updateStoreData"
        />
      </div>

      <div class="form-group">
        <label for="formType">Form Type <span class="required-asterisk">*</span></label>
        <div class="custom-select" :class="{ 'is-open': isFormTypeOpen, 'disabled': isEdit }">
          <div class="select-trigger" @click="!isEdit && toggleFormType()">
            <span v-if="formTypeselected">{{ formTypeselected.name }}</span>
            <span v-else class="placeholder">Select a form type</span>
            <svg v-if="!isEdit" class="dropdown-icon" :class="{ 'rotated': isFormTypeOpen }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else class="lock-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
              <path d="M12 11V17" stroke="currentColor" stroke-width="2"/>
            </svg>
          </div>
          <div v-if="isFormTypeOpen && !isEdit" class="dropdown-menu">
            <div class="search-container">
              <input
                ref="formTypeSearch"
                v-model="formTypeSearchQuery"
                type="text"
                placeholder="Search form types..."
                class="dropdown-search"
                @click.stop
              />
            </div>
            <div class="dropdown-items" ref="formTypeItems">
              <div
                v-for="type in filteredFormTypes"
                :key="type.id"
                class="dropdown-item"
                :class="{ 'is-selected': formTypeselected && formTypeselected.id === type.id }"
                :ref="formTypeselected && formTypeselected.id === type.id ? 'selectedFormType' : null"
                @click="selectFormType(type)"
              >
                <svg class="item-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                  <line x1="9" y1="9" x2="15" y2="9" stroke="currentColor" stroke-width="2"/>
                  <line x1="9" y1="15" x2="15" y2="15" stroke="currentColor" stroke-width="2"/>
                </svg>
                {{ type.name }}
              </div>
              <div v-if="filteredFormTypes.length === 0" class="no-results">
                No form types found
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="sequenceType">Sequence Type</label>
        <div class="custom-select" :class="{ 'is-open': isSequenceTypeOpen, 'disabled': isSequenceTypeDisabled }">
          <div class="select-trigger" @click="!isSequenceTypeDisabled && toggleSequenceType()">
            <span v-if="selectedSequenceType">{{ selectedSequenceType.name }}</span>
            <span v-else class="placeholder">Select sequence type</span>
            <svg class="dropdown-icon" :class="{ 'rotated': isSequenceTypeOpen }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div v-if="isSequenceTypeOpen" class="dropdown-menu">
            <div class="search-container">
              <input
                ref="sequenceTypeSearch"
                v-model="sequenceTypeSearchQuery"
                type="text"
                placeholder="Search sequence types..."
                class="dropdown-search"
                @click.stop
              />
            </div>
            <div class="dropdown-items" ref="sequenceTypeItems">
              <div
                v-for="sequence in filteredSequenceTypes"
                :key="sequence.id"
                class="dropdown-item"
                :class="{ 'is-selected': selectedSequenceType && selectedSequenceType.id === sequence.id }"
                :ref="selectedSequenceType && selectedSequenceType.id === sequence.id ? 'selectedSequence' : null"
                @click="selectSequenceType(sequence)"
              >
                <svg class="item-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none"/>
                </svg>
                {{ sequence.name }}
              </div>
              <div v-if="filteredSequenceTypes.length === 0" class="no-results">
                No sequence types found
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="formTypeselected?.id !== 5" class="form-group">
        <label for="workflow">Workflow <span v-if="formTypeselected?.id === 14" class="required-asterisk">*</span></label>
        <div class="custom-select" :class="{ 'is-open': isWorkflowOpen, 'open-upward': workflowDropdownDirection === 'up', 'disabled': isWorkflowDisabled }" ref="workflowSelect">
          <div class="select-trigger" @click="!isWorkflowDisabled && toggleWorkflow()" ref="workflowTrigger">
            <span v-if="selectedWorkflow">{{ selectedWorkflow.name }}</span>
            <span v-else class="placeholder">Select workflow</span>
            <svg class="dropdown-icon" :class="{ 'rotated': isWorkflowOpen }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div v-if="isWorkflowOpen" class="dropdown-menu" :class="{ 'dropdown-up': workflowDropdownDirection === 'up' }">
            <div class="search-container">
              <input
                ref="workflowSearch"
                v-model="workflowSearchQuery"
                type="text"
                placeholder="Search workflows..."
                class="dropdown-search"
                @click.stop
              />
            </div>
            <div class="dropdown-items" ref="workflowItems">
              <div
                v-for="workflow in filteredWorkflows"
                :key="workflow.id"
                class="dropdown-item"
                :class="{ 'is-selected': selectedWorkflow && selectedWorkflow.id === workflow.id }"
                :ref="selectedWorkflow && selectedWorkflow.id === workflow.id ? 'selectedWorkflowItem' : null"
                @click="selectWorkflow(workflow)"
              >
                <svg class="item-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 16V8A2 2 0 0 0 19 6H5A2 2 0 0 0 3 8V16A2 2 0 0 0 5 18H19A2 2 0 0 0 21 16Z" stroke="currentColor" stroke-width="2" fill="none"/>
                  <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" fill="none"/>
                </svg>
                {{ workflow.name }}
              </div>
              <div v-if="filteredWorkflows.length === 0" class="no-results">
                No workflows found
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="button-group">
      <button class="btn btn-black pointer" @click="handleSubmit"
        :disabled="(![11, 12].includes(editData?.formType) && !formTypeselected) || !templateName || (formTypeselected?.id === 14 && !workflowId)"
      >
        Next
      </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formTemplateNameCheck } from '@/api'
import { alert } from '@/plugins/notification'
import { mapGetters } from 'vuex'
import config from '@/config'

export default {
  data () {
    return {
      sequenceTypeId: '',
      formTypeselected: '',
      templateName: '',
      sequenceType: '',
      workflowId: '',
      selectedSequenceType: null,
      selectedWorkflow: null,
      isFormTypeOpen: false,
      isSequenceTypeOpen: false,
      isWorkflowOpen: false,
      workflowDropdownDirection: 'down',
      formTypeSearchQuery: '',
      sequenceTypeSearchQuery: '',
      workflowSearchQuery: ''
    }
  },
  props: {
    editData: {
      type: Object
    },
    workflows: {
      type: Array,
      default: () => []
    },
    sequenceTemplates: {
      type: Array,
      default: () => []
    },
    formType: {
      type: String
    },
    filteredFormTypeList: {
      type: Array,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('form', ['setupData']),
    filteredFormTypes () {
      if (!this.formTypeSearchQuery) return this.filteredFormTypeList
      return this.filteredFormTypeList.filter(type =>
        type.name.toLowerCase().includes(this.formTypeSearchQuery.toLowerCase())
      )
    },
    filteredSequenceTypes () {
      if (!this.sequenceTypeSearchQuery) return this.sequenceTemplates
      return this.sequenceTemplates.filter(sequence =>
        sequence.name.toLowerCase().includes(this.sequenceTypeSearchQuery.toLowerCase())
      )
    },
    filteredWorkflows () {
      let workflows = this.workflows
      if (this.formTypeselected && this.formTypeselected.id === 14) {
        workflows = workflows.filter(workflow => workflow.feature_id === 3 && workflow.category === 4)
      } else if (this.formTypeselected && this.formTypeselected.id !== 14) {
        workflows = workflows.filter(workflow => workflow.feature_id === 5)
      }
      if (!this.workflowSearchQuery) return workflows
      return workflows.filter(workflow =>
        workflow.name.toLowerCase().includes(this.workflowSearchQuery.toLowerCase())
      )
    },
    isWorkflowDisabled () {
      const formTypeId = Number(this.formTypeselected?.id || this.editData?.formType)
      return formTypeId === 5 || (this.isEdit && [11, 12].includes(formTypeId))
    },
    isSequenceTypeDisabled () {
      return [11, 12].includes(Number(this.editData?.formType))
    }
  },
  watch: {
    editData: {
      handler (newData) { if (this.isEdit && newData) this.populateEditData() },
      immediate: true,
      deep: true
    },
    setupData: {
      handler (newData) { if (!this.isEdit && newData && Object.keys(newData).length > 0) this.populateStoreData() },
      immediate: true,
      deep: true
    },
    sequenceTemplates: {
      handler () { this.refreshData() }
    },
    workflows: {
      handler () { this.refreshData() }
    },
    filteredFormTypeList: {
      handler () { this.refreshData() }
    }
  },
  mounted () {
    document.addEventListener('click', this.handleClickOutside)
    this.refreshData()
  },
  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    refreshData () {
      if (this.isEdit && this.editData) this.populateEditData()
      else if (!this.isEdit && this.setupData) this.populateStoreData()
    },
    calculateDropdownDirection (triggerElement) {
      const triggerRect = triggerElement.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const dropdownHeight = 200
      const spaceBelow = viewportHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top
      return (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) ? 'up' : 'down'
    },
    clearSearchQueries () {
      this.formTypeSearchQuery = ''
      this.sequenceTypeSearchQuery = ''
      this.workflowSearchQuery = ''
    },
    scrollToSelected (refName) {
      this.$nextTick(() => {
        const element = this.$refs[refName]
        const target = Array.isArray(element) ? element[0] : element
        if (target) {
          target.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    populateStoreData () {
      if (!this.setupData) return
      this.templateName = this.setupData.templateName || ''
      this.formTypeselected = this.setupData.formTypeselected || ''
      this.selectedSequenceType = this.setupData.selectedSequenceType || null
      this.selectedWorkflow = this.setupData.selectedWorkflow || null
      this.sequenceTypeId = this.setupData.sequenceTypeId || ''
      this.workflowId = this.setupData.workflowId || ''
    },
    updateStoreData () {
      this.$store.dispatch('form/updateSetupData', {
        templateName: this.templateName,
        formTypeselected: this.formTypeselected,
        selectedSequenceType: this.selectedSequenceType,
        selectedWorkflow: this.selectedWorkflow,
        sequenceTypeId: this.sequenceTypeId,
        workflowId: this.workflowId
      })
    },
    populateEditData () {
      if (!this.editData) return
      this.templateName = this.editData.name || this.editData.templateName || ''
      const sequenceId = this.editData.core_sequence_id_template?.id || this.editData.sequence_template_id
      if (sequenceId && this.sequenceTemplates.length) {
        this.selectedSequenceType = this.sequenceTemplates.find(seq => seq.id === sequenceId) || null
        this.sequenceTypeId = sequenceId
      }
      const workflowId = this.editData.workflow_template?.id || this.editData.workflowId || this.editData.workflow_template_id
      if (workflowId && this.workflows.length) {
        this.selectedWorkflow = this.workflows.find(w => w.id === workflowId) || null
        this.workflowId = workflowId
      }
      if (this.filteredFormTypeList.length) {
        const formTypeData = this.editData.core_form_type || this.editData.formType
        if (formTypeData) {
          const targetId = typeof formTypeData === 'object' ? formTypeData.id : formTypeData
          const selectedFromList = this.filteredFormTypeList.find(type => type.id === targetId)
          // System generated form types (e.g. 11/12) are intentionally filtered out
          // from selectable list, but must still be displayed during edit.
          this.formTypeselected = selectedFromList || (typeof formTypeData === 'object'
            ? formTypeData
            : (this.editData.formTypeName ? { id: targetId, name: this.editData.formTypeName } : null))
        }
      }
    },
    toggleFormType () {
      if (this.isEdit) return
      this.isFormTypeOpen = !this.isFormTypeOpen
      this.isSequenceTypeOpen = false
      this.isWorkflowOpen = false
      if (this.isFormTypeOpen) {
        this.clearSearchQueries()
        this.$nextTick(() => {
          if (this.$refs.formTypeSearch) this.$refs.formTypeSearch.focus()
          if (this.formTypeselected) this.scrollToSelected('selectedFormType')
        })
      }
    },
    toggleSequenceType () {
      if (this.isSequenceTypeDisabled) return
      this.isSequenceTypeOpen = !this.isSequenceTypeOpen
      this.isFormTypeOpen = false
      this.isWorkflowOpen = false
      if (this.isSequenceTypeOpen) {
        this.clearSearchQueries()
        this.$nextTick(() => {
          if (this.$refs.sequenceTypeSearch) this.$refs.sequenceTypeSearch.focus()
          if (this.selectedSequenceType) this.scrollToSelected('selectedSequence')
        })
      }
    },
    toggleWorkflow () {
      if (this.isWorkflowDisabled) return
      if (!this.isWorkflowOpen && this.$refs.workflowTrigger) {
        this.workflowDropdownDirection = this.calculateDropdownDirection(this.$refs.workflowTrigger)
      }
      this.isWorkflowOpen = !this.isWorkflowOpen
      this.isFormTypeOpen = false
      this.isSequenceTypeOpen = false
      if (this.isWorkflowOpen) {
        this.clearSearchQueries()
        this.$nextTick(() => {
          if (this.$refs.workflowSearch) this.$refs.workflowSearch.focus()
          if (this.selectedWorkflow) this.scrollToSelected('selectedWorkflowItem')
        })
      }
    },
    selectFormType (type) {
      if (this.isEdit) return
      if (this.formTypeselected && this.formTypeselected.id === type.id) {
        this.formTypeselected = ''
      } else {
        this.formTypeselected = type
        if (type.id === config.FORM_TEMPLATE_TYPE.JOF) {
          const defaultSequence = this.sequenceTemplates.find(seq => (seq.system_generated && seq.name === 'Job Order Default Sequence Template'))
          this.selectedSequenceType = defaultSequence || null
          this.sequenceTypeId = defaultSequence ? defaultSequence.id : ''
        } else {
          this.selectedSequenceType = null
          this.sequenceTypeId = ''
        }
        this.selectedWorkflow = null
        this.workflowId = ''
      }
      this.isFormTypeOpen = false
      this.clearSearchQueries()
      this.updateStoreData()
    },
    selectSequenceType (sequence) {
      if (this.selectedSequenceType && this.selectedSequenceType.id === sequence.id) {
        this.selectedSequenceType = null
        this.sequenceTypeId = ''
      } else {
        this.selectedSequenceType = sequence
        this.sequenceTypeId = sequence.id
      }
      this.isSequenceTypeOpen = false
      this.clearSearchQueries()
      this.updateStoreData()
    },
    selectWorkflow (workflow) {
      if (this.selectedWorkflow && this.selectedWorkflow.id === workflow.id) {
        this.selectedWorkflow = null
        this.workflowId = ''
      } else {
        this.selectedWorkflow = workflow
        this.workflowId = workflow.id
      }
      this.isWorkflowOpen = false
      this.clearSearchQueries()
      this.updateStoreData()
    },
    handleClickOutside (event) {
      const dropdownElements = this.$el.querySelectorAll('.custom-select')
      let clickedInsideAnyDropdown = false
      dropdownElements.forEach(dropdown => {
        if (dropdown.contains(event.target)) clickedInsideAnyDropdown = true
      })
      if (!clickedInsideAnyDropdown) {
        this.isFormTypeOpen = false
        this.isSequenceTypeOpen = false
        this.isWorkflowOpen = false
        this.clearSearchQueries()
      }
    },
    handleSubmit () {
      if (this.isEdit) {
        this.$emit('create-template', {
          formTypeName: this.formTypeselected?.name,
          templateName: this.templateName,
          sequenceType: this.sequenceTypeId,
          workflowId: this.workflowId
        })
        return
      }
      formTemplateNameCheck(this.templateName).then(res => {
        if (res.core_form_templates.length) {
          alert('Template name already exists.')
        } else {
          this.$emit('create-template', {
            formType: this.formTypeselected?.id,
            formTypeName: this.formTypeselected?.name,
            templateName: this.templateName,
            sequenceType: this.sequenceTypeId,
            workflowId: this.workflowId
          })
        }
      })
    }
  }
}
</script>

<style scoped>
/* Add disabled state styling */
.custom-select.disabled .select-trigger {
  background-color: #f3f4f6 !important;
  color: #9ca3af;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

.custom-select.disabled .select-trigger:hover {
  background-color: #f3f4f6 !important;
  border-color: #e5e7eb !important;
}
.custom-select.disabled,
.select-trigger.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none; /* Alternative to the click condition */
}

.lock-icon {
  width: 16px;
  height: 16px;
  color: #9ca3af;
}

/* Search container styling */
.search-container {
  position: relative;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.dropdown-search {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: var(--text-s);
  outline: none;
  background-color: #f9fafb;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.dropdown-search:focus {
  border-color: #3b82f6;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Dropdown items container */
.dropdown-items {
  max-height: 160px;
  overflow-y: auto;
}

/* No results styling */
.no-results {
  padding: 12px 14px;
  text-align: center;
  color: #9ca3af;
  font-size: var(--text-s);
  font-style: italic;
}

/* Rest of your existing styles stay the same */
.template-setup {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 20px;
  box-sizing: border-box;
}

.template-box {
  width: 100%;
  max-width: 420px;
  min-width: 350px;
  padding: 8px 20px 16px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
}

.title {
  text-align: center;
  font-size: var(--text-l);
  font-weight: 600;
  margin-block-start: 6px;
  margin-block-end: 10px;
  color: #111827;
}

.description {
  text-align: center;
  font-size: var(--text-xs);
  margin-top: 5px;
  margin-bottom: 15px;
  color: #6b7280;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: var(--text-s);
  font-weight: 500;
  margin-bottom: 6px;
  /* color: #374151; */
}

.form-group input {
  width: 100%;
  padding: 10px 14px;
  font-size: var(--text-s);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: #f9fafb;
}

.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: white;
}

.custom-select {
  position: relative;
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: var(--text-s);
}

.select-trigger:hover {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

.custom-select.is-open .select-trigger {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: white;
}

.placeholder {
  color: #9ca3af;
}

.dropdown-icon {
  width: 15px;
  height: 15px;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  max-height: 240px;
  overflow: hidden;
}

/* Smart dropdown positioning for upward opening */
.dropdown-menu.dropdown-up {
  top: auto;
  bottom: calc(100% + 4px);
}

/* Flip arrow when dropdown opens upward */
.custom-select.open-upward.is-open .dropdown-icon {
  transform: rotate(0deg);
}

.custom-select.open-upward:not(.is-open) .dropdown-icon {
  transform: rotate(180deg);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  font-size: var(--text-s);
  color: #374151;
  transition: all 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.dropdown-item:active {
  background-color: #e5e7eb;
}

.item-icon {
  width: 16px;
  height: 16px;
  margin-right: 10px;
  color: #6b7280;
  flex-shrink: 0;
}

.dropdown-item:hover .item-icon {
  color: #374151;
}

.button-group {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: var(--text-s);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-black {
  background-color: #111827;
  color: white;
}

.btn-black:hover:not(:disabled) {
  background-color: #1f2937;
  transform: translateY(-1px);
}

.btn-black:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.required-asterisk {
  color: #ef4444;
  margin-left: 2px;
}

/* Scrollbar styling for dropdown items */
.dropdown-items::-webkit-scrollbar {
  width: 6px;
}

.dropdown-items::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 6px;
}

.dropdown-items::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 6px;
}

.dropdown-items::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
.dropdown-item.is-selected {
  background-color: #f3f4f6;
  color: #000;
  font-weight: 600;
}

.dropdown-item.is-selected .item-icon {
  color: #000;
}
</style>
